import React, { useState } from 'react';
import TextField from '@mui/material/TextField';
import Select from 'react-select';
import FormControl from '@mui/material/FormControl';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import Grid from "@mui/material/Grid2";
import { Typography } from '@mui/material';
import { getInputProps } from 'pages/DashboardHelpers';
import { useDispatch } from 'react-redux';
import { createModelHierarchy, getModelHierarchy } from 'actions/userActions';

const formControlStyles = {
  display: 'flex',
  width: '100%',
  marginBottom: '14px',
};

const formFieldStyles = {
  control: (provided) => ({ ...provided, padding: '6px', borderRadius: "12px" }),
};

const FormField = ({
  label,
  value,
  onChange,
  options,
  placeholder,
  isSelect = false,
  isDisabled = false
}) => (
  <Grid container spacing={2} alignItems="center" sx={formControlStyles}>
    <Grid item xs={4} sx={{ width: '90px' }}>
      <Typography variant="body1">{label}</Typography>
    </Grid>
    <Grid item xs={8} sx={{ flex: 1 }}>
      {isSelect ? (
        <FormControl fullWidth>
          <Select
            styles={formFieldStyles}
            placeholder={placeholder}
            value={value}
            options={options}
            onChange={onChange}
            menuPosition="fixed"
            isClearable={false}
            isDisabled={isDisabled}
          />
        </FormControl>
      ) : (
        <TextField
          fullWidth
          variant="outlined"
          placeholder={placeholder}
          value={value}
          onChange={onChange}
          slotProps={{ input: getInputProps() }}
          disabled={isDisabled}
        />
      )}
    </Grid>
  </Grid>
);

export const AddModelHrcyDialog = ({
  open,
  onClose,
  regions,
  seriesList: initialSeriesList,
  subCategoryList : initialSubCategoryList,
  email
}) => {
  const dispatch = useDispatch();

  const [categoryList, setCategoryList] = useState([]);
  const [seriesList, setSeriesList] = useState(initialSeriesList || []);
  const [subCategoryList, setSubCategoryList] = useState(initialSubCategoryList || []);

  const regionOptions = regions?.map(r => ({ label: r, value: r })) || [];
  const categoryOptions = categoryList?.map(c => ({ label: c, value: c })) || [];
  const seriesOptions = seriesList?.map(s => ({ label: s, value: s })) || [];
  const subCategoryOptions = subCategoryList?.map(sc => ({ label: sc, value: sc })) || [];

  const [kmatId, setKmatId] = useState('');
  const [kmatError, setKmatError] = useState('');
  const [modelName, setModelName] = useState('');
  const [region, setRegion] = useState(null);
  const [category, setCategory] = useState(null);
  const [series, setSeries] = useState(null);
  const [subCategory, setSubCategory] = useState(null);

 const handleCategoryChange = async (selected) => {
  setCategory(selected);
  setSeries(null);
  setSubCategory(null);

  if (selected) {
    const response = await dispatch(getModelHierarchy({ category: selected.value, series: "" }));
    const selectedSeries =response?.selectedSeries || [];
    setSeriesList(selectedSeries);
  } else {
    setSeriesList([]);
  }
};

const handleSeriesChange = async (selected) => {
  setSeries(selected);
  setSubCategory(null);
  if (selected) {
    const response = await dispatch(getModelHierarchy({ category: category?.value || "", series: selected.value }));
    const selectedSubCategories = response?.selectedSubCategory || [];
    setSubCategoryList(selectedSubCategories);
  } else {
    setSubCategoryList([]);
  }
};


  const handleAddModelHierarchy = () => {
    dispatch(createModelHierarchy(kmatId, region?.value, category?.value, series?.value, modelName, subCategory?.value, email));
      onClose();
  };

  const validateKmatId = (value) => {
    const regex = /^[a-zA-Z0-9]{6}-999$/;
    if (!regex.test(value)) {
      return "KMAT ID must be 6 alphanumeric characters ending with '-999'";
    }
    return '';
  };

  const handleKmatIdChange = async (e) => {
    const value = e.target.value.toUpperCase();
    setKmatId(value);
    const error = validateKmatId(value);
    setKmatError(error);
    //Note: Reset Dependent selection
    setCategory(null);
    setSeries(null);
    setSubCategory(null);
    setSeriesList([]);
    setSubCategoryList([]);
 

    if (!error && value) {
      const response = await dispatch(getModelHierarchy({ category: "", series: "", kmat_id: value }));
      setCategoryList(response?.categoryList || []);
    } else {
      setCategoryList([]);
    }
  };

  const isSeriesDisabled = !category;
  const isSubCategoryDisabled = !series;
  const isAddDisabled =
    !region ||
    !category ||
    !series ||
    !subCategory ||
    !modelName ||
    !kmatId ||
    !!kmatError;

  return (
    <Dialog open={open} onClose={onClose} className='custom-dialog'>
      <DialogTitle variant='h6'>{"Add Model Hierarchy"}</DialogTitle>
      <DialogContent>
        {/* Region */}
        <FormField
          label="Region"
          value={region}
          onChange={(selected) => setRegion(selected)}
          options={regionOptions}
          placeholder="Select Region"
          isSelect={true}
        />

        {/* KMAT ID */}
        <FormField
          label="KMAT ID"
          value={kmatId}
          onChange={handleKmatIdChange}
          placeholder="Enter KMAT ID"
        />
        {kmatError && (
          <Typography color="error" variant="caption" sx={{ ml: 2 }}>
            {kmatError}
          </Typography>
        )}

        {/* Category */}
        <FormField
          label="Category"
          value={category}
          onChange={handleCategoryChange}
          options={categoryOptions}
          placeholder="Select Category"
          isSelect={true}
        />

        {/* Series */}
        <FormField
          label="Series"
          value={series}
          onChange={handleSeriesChange}
          options={seriesOptions}
          placeholder="Select Series"
          isSelect={true}
          isDisabled={isSeriesDisabled}
        />

        {/* Model Name */}
        <FormField
          label="Model Name"
          value={modelName}
          onChange={(e) => setModelName(e.target.value)}
          placeholder="Enter Model Name"
        />

        {/* Sub Category */}
        <FormField
          label="Sub Category"
          value={subCategory}
          onChange={(selected) => setSubCategory(selected)}
          options={subCategoryOptions}
          placeholder="Select Sub Category"
          isSelect={true}
          isDisabled={isSubCategoryDisabled}
        />
      </DialogContent>

      <DialogActions className='dialog-actions'>
        <Button variant="outlined" className='cancel-button' onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          onClick={handleAddModelHierarchy}
          className='add-Button'
          disabled={isAddDisabled}
        >
          +&nbsp; Add
        </Button>
      </DialogActions>
    </Dialog>
  );
};